/**
 * Discoverability DB
 *
 * Server-side persistence for feature usage and prompt analytics.
 *
 * Primary responsibilities:
 * - Mark feature first-use and repeat-use
 * - Record tooltip, prompt, and onboarding events
 * - Return typed usage snapshots for the trigger engine
 *
 * Dependencies:
 * - Supabase service client
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'
import type {
  DiscoverabilityCustomFeature,
  DiscoverabilitySelectorCandidate
} from '@/lib/discoverability/custom-features'
import type { DiscoverabilityTrackedFeatureKey } from '@/lib/discoverability/features'
import type {
  DiscoverabilityPlanGate,
  DiscoverabilityPromptKind,
  DiscoverabilitySurface
} from '@/lib/discoverability/features'
import { isDiscoverabilityTrackedFeatureKey } from '@/lib/discoverability/features'
import type { DiscoverabilityUsageSnapshot } from '@/lib/discoverability/getDiscoverabilityPrompts'
import { ProductAnalyticsEventNames } from '@/lib/analytics/product-events'
import { trackServer } from '@/lib/analytics/track'
import type { Database, Json } from '@/supabase/types'

const DISCOVERABILITY_POSTHOG_EVENT_BY_TYPE: Partial<
  Record<DiscoverabilityEventType, (typeof ProductAnalyticsEventNames)[keyof typeof ProductAnalyticsEventNames]>
> = {
  feature_used: ProductAnalyticsEventNames.FEATURE_USED,
  prompt_shown: ProductAnalyticsEventNames.FEATURE_DISCOVERY_PROMPT_SHOWN,
  prompt_clicked: ProductAnalyticsEventNames.FEATURE_DISCOVERY_PROMPT_CLICKED,
  prompt_dismissed: ProductAnalyticsEventNames.FEATURE_DISCOVERY_PROMPT_DISMISSED,
  tooltip_shown: ProductAnalyticsEventNames.FEATURE_DISCOVERY_TOOLTIP_SHOWN,
  tooltip_clicked: ProductAnalyticsEventNames.FEATURE_DISCOVERY_TOOLTIP_CLICKED,
  tooltip_dismissed: ProductAnalyticsEventNames.FEATURE_DISCOVERY_TOOLTIP_DISMISSED,
  onboarding_completed: ProductAnalyticsEventNames.FEATURE_ONBOARDING_COMPLETED
}

async function captureDiscoverabilityPostHog(input: {
  userId: string
  featureKey: DiscoverabilityTrackedFeatureKey
  eventType: DiscoverabilityEventType
  surface?: string | null
  trigger?: string | null
}) {
  const eventName = DISCOVERABILITY_POSTHOG_EVENT_BY_TYPE[input.eventType]
  if (!eventName) return

  try {
    await trackServer(eventName, {
      user_id: input.userId,
      feature_key: input.featureKey,
      surface: input.surface ?? undefined,
      trigger: input.trigger ?? undefined,
      source: 'discoverability'
    })
  } catch {
    // Analytics must never block feature usage persistence.
  }
}

export type DiscoverabilityEventType =
  | 'prompt_shown'
  | 'prompt_clicked'
  | 'prompt_dismissed'
  | 'tooltip_shown'
  | 'tooltip_clicked'
  | 'tooltip_dismissed'
  | 'onboarding_completed'
  | 'feature_used'

export const DISCOVERABILITY_EVENT_TYPES: DiscoverabilityEventType[] = [
  'prompt_shown',
  'prompt_clicked',
  'prompt_dismissed',
  'tooltip_shown',
  'tooltip_clicked',
  'tooltip_dismissed',
  'onboarding_completed',
  'feature_used'
]

type UserFeatureUsageRow = {
  id: string
  user_id: string
  feature_key: string
  first_used_at: string | null
  last_used_at: string | null
  use_count: number
  dismissed_prompt_count: number
  completed_onboarding_at: string | null
  last_prompted_at: string | null
  created_at: string
  updated_at: string
}

type UserFeatureUsageInsert = {
  user_id: string
  feature_key: string
  first_used_at?: string | null
  last_used_at?: string | null
  use_count?: number
  dismissed_prompt_count?: number
  completed_onboarding_at?: string | null
  last_prompted_at?: string | null
}

type UserFeatureUsageUpdate = Partial<
  Pick<
    UserFeatureUsageRow,
    | 'first_used_at'
    | 'last_used_at'
    | 'use_count'
    | 'dismissed_prompt_count'
    | 'completed_onboarding_at'
    | 'last_prompted_at'
  >
>

type UserDiscoverabilityEventRow = {
  id: string
  user_id: string | null
  feature_key: string
  event_type: DiscoverabilityEventType
  surface: string | null
  trigger: string | null
  metadata: Json
  created_at: string
}

type UserDiscoverabilityEventInsert = {
  user_id: string
  feature_key: string
  event_type: DiscoverabilityEventType
  surface?: string | null
  trigger?: string | null
  metadata?: Json
}

type DiscoverabilityCustomFeatureRow = {
  id: string
  feature_key: string
  prompt_kind: DiscoverabilityPromptKind
  minimum_plan: DiscoverabilityPlanGate
  title: string
  body: string
  cta_label: string
  cta_href: string
  surface: DiscoverabilitySurface
  final_selector: string
  selector_path: Json
  route_hint: string | null
  trigger: string | null
  metadata: Json
  enabled: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

type DiscoverabilityCustomFeatureInsert = {
  feature_key: string
  prompt_kind?: DiscoverabilityPromptKind
  minimum_plan?: DiscoverabilityPlanGate
  title: string
  body: string
  cta_label: string
  cta_href?: string
  surface?: DiscoverabilitySurface
  final_selector: string
  selector_path?: Json
  route_hint?: string | null
  trigger?: string | null
  metadata?: Json
  enabled?: boolean
  created_by?: string | null
}

type DiscoverabilityCustomFeatureUpdate = Partial<
  Omit<DiscoverabilityCustomFeatureInsert, 'feature_key' | 'created_by'>
>

type DiscoverabilityDatabase = Database & {
  public: Database['public'] & {
    Tables: Database['public']['Tables'] & {
      user_feature_usage: {
        Row: UserFeatureUsageRow
        Insert: UserFeatureUsageInsert
        Update: UserFeatureUsageUpdate
        Relationships: []
      }
      user_discoverability_events: {
        Row: UserDiscoverabilityEventRow
        Insert: UserDiscoverabilityEventInsert
        Update: Partial<UserDiscoverabilityEventInsert>
        Relationships: []
      }
      discoverability_custom_features: {
        Row: DiscoverabilityCustomFeatureRow
        Insert: DiscoverabilityCustomFeatureInsert
        Update: DiscoverabilityCustomFeatureUpdate
        Relationships: []
      }
    }
  }
}

type DiscoverabilityClient = SupabaseClient<DiscoverabilityDatabase>

const createTimestamp = () => new Date().toISOString()

export const isDiscoverabilityEventType = (value: unknown): value is DiscoverabilityEventType => {
  return (
    typeof value === 'string' &&
    DISCOVERABILITY_EVENT_TYPES.includes(value as DiscoverabilityEventType)
  )
}

function getDiscoverabilityClient(): DiscoverabilityClient {
  return createServiceClient() as unknown as DiscoverabilityClient
}

function toUsageSnapshot(row: UserFeatureUsageRow): DiscoverabilityUsageSnapshot | null {
  if (!isDiscoverabilityTrackedFeatureKey(row.feature_key)) {
    return null
  }

  return {
    featureKey: row.feature_key,
    firstUsedAt: row.first_used_at,
    lastUsedAt: row.last_used_at,
    useCount: row.use_count,
    dismissedPromptCount: row.dismissed_prompt_count,
    completedOnboardingAt: row.completed_onboarding_at,
    lastPromptedAt: row.last_prompted_at
  }
}

function isSelectorCandidate(value: unknown): value is DiscoverabilitySelectorCandidate {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const record = value as Record<string, unknown>
  return typeof record.selector === 'string' && typeof record.label === 'string'
}

function toSelectorPath(value: Json): DiscoverabilitySelectorCandidate[] {
  if (!Array.isArray(value)) return []

  return value.filter(isSelectorCandidate)
}

function toCustomFeature(row: DiscoverabilityCustomFeatureRow): DiscoverabilityCustomFeature {
  return {
    id: row.id,
    featureKey: row.feature_key as DiscoverabilityCustomFeature['featureKey'],
    kind: row.prompt_kind,
    minimumPlan: row.minimum_plan,
    title: row.title,
    body: row.body,
    ctaLabel: row.cta_label,
    ctaHref: row.cta_href,
    surface: row.surface,
    finalSelector: row.final_selector,
    selectorPath: toSelectorPath(row.selector_path),
    routeHint: row.route_hint,
    trigger: row.trigger,
    enabled: row.enabled,
    updatedAt: row.updated_at
  }
}

async function getFeatureUsageRow(
  supabase: DiscoverabilityClient,
  userId: string,
  featureKey: DiscoverabilityTrackedFeatureKey
) {
  const { data, error } = await supabase
    .from('user_feature_usage')
    .select('*')
    .eq('user_id', userId)
    .eq('feature_key', featureKey)
    .maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return data
}

async function ensureFeatureUsageRow(
  supabase: DiscoverabilityClient,
  userId: string,
  featureKey: DiscoverabilityTrackedFeatureKey
) {
  const existing = await getFeatureUsageRow(supabase, userId, featureKey)

  if (existing) {
    return existing
  }

  const { data, error } = await supabase
    .from('user_feature_usage')
    .insert({
      user_id: userId,
      feature_key: featureKey
    })
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data
}

async function updateFeatureUsageRow(
  supabase: DiscoverabilityClient,
  userId: string,
  featureKey: DiscoverabilityTrackedFeatureKey,
  updates: UserFeatureUsageUpdate
) {
  const { data, error } = await supabase
    .from('user_feature_usage')
    .update(updates)
    .eq('user_id', userId)
    .eq('feature_key', featureKey)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data
}

async function insertDiscoverabilityEvent(
  supabase: DiscoverabilityClient,
  input: UserDiscoverabilityEventInsert
) {
  const { data, error } = await supabase
    .from('user_discoverability_events')
    .insert(input)
    .select('id')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data.id
}

// --------------

export async function listUserFeatureUsage(userId: string) {
  const supabase = getDiscoverabilityClient()
  const { data, error } = await supabase
    .from('user_feature_usage')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })

  if (error) {
    throw new Error(error.message)
  }

  return (data ?? []).reduce<
    Partial<Record<DiscoverabilityTrackedFeatureKey, DiscoverabilityUsageSnapshot>>
  >((usage, row) => {
    const snapshot = toUsageSnapshot(row)
    if (snapshot) {
      usage[snapshot.featureKey] = snapshot
    }

    return usage
  }, {})
}

/**
 * Load usage, backfill from product evidence (Spaces/files/planner/…), reload.
 * Use on GET /usage and chat prompt build so power users stop looking "new".
 */
export async function listUserFeatureUsageWithProductSeed(userId: string) {
  const usage = await listUserFeatureUsage(userId)

  try {
    const { seedDiscoverabilityUsageFromProduct } = await import(
      '@/lib/discoverability/seed-from-product'
    )
    const seeded = await seedDiscoverabilityUsageFromProduct(userId, usage)
    if (seeded.length === 0) return usage
  } catch {
    return usage
  }

  return listUserFeatureUsage(userId)
}

// --------------

export async function markFeatureUsed(input: {
  userId: string
  featureKey: DiscoverabilityTrackedFeatureKey
  surface?: string | null
  trigger?: string | null
  metadata?: Json
}) {
  const supabase = getDiscoverabilityClient()
  const existing = await ensureFeatureUsageRow(supabase, input.userId, input.featureKey)
  const now = createTimestamp()

  const usage = await updateFeatureUsageRow(supabase, input.userId, input.featureKey, {
    first_used_at: existing.first_used_at ?? now,
    last_used_at: now,
    use_count: existing.use_count + 1
  })

  const eventId = await insertDiscoverabilityEvent(supabase, {
    user_id: input.userId,
    feature_key: input.featureKey,
    event_type: 'feature_used',
    surface: input.surface ?? null,
    trigger: input.trigger ?? null,
    metadata: input.metadata ?? {}
  })

  await captureDiscoverabilityPostHog({
    userId: input.userId,
    featureKey: input.featureKey,
    eventType: 'feature_used',
    surface: input.surface,
    trigger: input.trigger
  })

  return {
    eventId,
    usage: toUsageSnapshot(usage)
  }
}

// --------------

export async function recordDiscoverabilityEvent(input: {
  userId: string
  featureKey: DiscoverabilityTrackedFeatureKey
  eventType: DiscoverabilityEventType
  surface?: string | null
  trigger?: string | null
  metadata?: Json
}) {
  const supabase = getDiscoverabilityClient()
  const existing = await ensureFeatureUsageRow(supabase, input.userId, input.featureKey)
  const now = createTimestamp()

  const eventId = await insertDiscoverabilityEvent(supabase, {
    user_id: input.userId,
    feature_key: input.featureKey,
    event_type: input.eventType,
    surface: input.surface ?? null,
    trigger: input.trigger ?? null,
    metadata: input.metadata ?? {}
  })

  // Tip peek/dismiss must not burn card cooldown or journey dismiss budget.
  if (input.eventType === 'prompt_shown') {
    await updateFeatureUsageRow(supabase, input.userId, input.featureKey, {
      last_prompted_at: now
    })
  }

  if (input.eventType === 'prompt_dismissed') {
    // Stamp last_prompted_at so the 14-day dismiss snooze starts at "Later".
    await updateFeatureUsageRow(supabase, input.userId, input.featureKey, {
      dismissed_prompt_count: existing.dismissed_prompt_count + 1,
      last_prompted_at: now
    })
  }

  // CTA click = discovered. Single write here (card must not also POST /usage).
  if (input.eventType === 'prompt_clicked') {
    await updateFeatureUsageRow(supabase, input.userId, input.featureKey, {
      first_used_at: existing.first_used_at ?? now,
      last_used_at: now,
      use_count: existing.use_count + 1,
      completed_onboarding_at: existing.completed_onboarding_at ?? now
    })
  }

  if (input.eventType === 'onboarding_completed') {
    await updateFeatureUsageRow(supabase, input.userId, input.featureKey, {
      completed_onboarding_at: now
    })
  }

  await captureDiscoverabilityPostHog({
    userId: input.userId,
    featureKey: input.featureKey,
    eventType: input.eventType,
    surface: input.surface,
    trigger: input.trigger
  })

  return { eventId }
}

// --------------

export async function listDiscoverabilityCustomFeatures(options: { enabledOnly?: boolean } = {}) {
  const supabase = getDiscoverabilityClient()
  let query = supabase
    .from('discoverability_custom_features')
    .select('*')
    .order('updated_at', { ascending: false })

  if (options.enabledOnly) {
    query = query.eq('enabled', true)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(error.message)
  }

  return (data ?? []).map(toCustomFeature)
}

// --------------

export async function createDiscoverabilityCustomFeature(input: {
  featureKey: DiscoverabilityCustomFeature['featureKey']
  kind: DiscoverabilityPromptKind
  minimumPlan: DiscoverabilityPlanGate
  title: string
  body: string
  ctaLabel: string
  ctaHref: string
  surface: DiscoverabilitySurface
  finalSelector: string
  selectorPath: DiscoverabilitySelectorCandidate[]
  routeHint?: string | null
  trigger?: string | null
  enabled?: boolean
  createdBy: string
}) {
  const supabase = getDiscoverabilityClient()

  const { data, error } = await supabase
    .from('discoverability_custom_features')
    .insert({
      feature_key: input.featureKey,
      prompt_kind: input.kind,
      minimum_plan: input.minimumPlan,
      title: input.title,
      body: input.body,
      cta_label: input.ctaLabel,
      cta_href: input.ctaHref,
      surface: input.surface,
      final_selector: input.finalSelector,
      selector_path: input.selectorPath as unknown as Json,
      route_hint: input.routeHint ?? null,
      trigger: input.trigger ?? null,
      enabled: input.enabled ?? true,
      created_by: input.createdBy
    })
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return toCustomFeature(data)
}

// --------------

export async function updateDiscoverabilityCustomFeature(input: {
  id: string
  kind?: DiscoverabilityPromptKind
  minimumPlan?: DiscoverabilityPlanGate
  title?: string
  body?: string
  ctaLabel?: string
  ctaHref?: string
  surface?: DiscoverabilitySurface
  finalSelector?: string
  selectorPath?: DiscoverabilitySelectorCandidate[]
  routeHint?: string | null
  trigger?: string | null
  enabled?: boolean
}) {
  const supabase = getDiscoverabilityClient()

  const updates: DiscoverabilityCustomFeatureUpdate = {
    prompt_kind: input.kind,
    minimum_plan: input.minimumPlan,
    title: input.title,
    body: input.body,
    cta_label: input.ctaLabel,
    cta_href: input.ctaHref,
    surface: input.surface,
    final_selector: input.finalSelector,
    selector_path: input.selectorPath as unknown as Json | undefined,
    route_hint: input.routeHint,
    trigger: input.trigger,
    enabled: input.enabled
  }

  Object.keys(updates).forEach((key) => {
    const updateKey = key as keyof DiscoverabilityCustomFeatureUpdate
    if (updates[updateKey] === undefined) {
      delete updates[updateKey]
    }
  })

  const { data, error } = await supabase
    .from('discoverability_custom_features')
    .update(updates)
    .eq('id', input.id)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return toCustomFeature(data)
}
